// apps/client/src/ui/screens/decorate.tsx (M1-T16) — decorate mode (DESIGN.md "Decorate mode"): the room (or your yard) stays in view
// with its floor grid; Done is a primary button top-right; Turn and Put-away round controls float just above the lifted piece (below or
// beside it when that would put them under the top HUD row or off screen, kit/floating.ts); the tray is a 66cqw panel bottom-centre,
// at most 42cqh tall, whose category tabs stay pinned while its 8-up slot grid (or surface rows) scrolls (trayItems). Tapping a slot
// lifts that piece; tap the floor to set it down. In a room the Wallpaper and Floors tabs apply a pocket surface (room.surface).
// Opened from the Menu inside your own house or in your own yard; leaving the space (or a rebuilt scene) closes it.
// M1 playtest (844 × 390, a 6 × 6 cottage): the tray covered the front floor rows and the controls took taps meant for the floor. In a
// room the camera now frames it above the tray (game.frameForDecorate), and the controls go beside the floor, never over the tiles
// around the piece.
import { getEntry, getItem, tuning, type FishEntry, type StringKey } from '@whiskerton/content';
import { footprintRect, inRect, type ItemId, type ItemView, type Plot, type SpaceId, type TileXY } from '@whiskerton/shared';
import { checkYardPlacement, footprintOf, type Placed } from '@whiskerton/shared/rules/placement';
import { useEffect, useRef, useState, type RefObject } from 'react';
import * as THREE from 'three';
import { useShallow } from 'zustand/react/shallow';
import { buildTank } from '../../assets/builders/tank';
import type { Game } from '../../game/game';
import { getGame } from '../../game/instance';
import type { SpaceScene } from '../../game/spaces';
import { requestFor, surfaceCheck, trayItems, type DecorateSlice } from '../../state/slices/decorate';
import { useGame } from '../../state/store';
import type { GameStore } from '../../state/types';
import { createDecorator, type DecorSurface, type Held } from '../../world/decorate';
import { itemsLayerOf } from '../../world/items';
import { isRoomScene } from '../../world/spaces/room';
import { itemName, useT } from '../i18n';
import { useIcon } from '../items/useIcon';
import { Button, Panel, RoundButton, Slot, Tabs } from '../kit';
import { placeFloating } from '../kit/floating';
import type { ScreenDef } from '../router';

declare module '../router' { interface ScreenParams { decorate: undefined } }

type Tab = DecorateSlice['decorateTab'];
type Decorator = ReturnType<typeof createDecorator>;
/** What one decorate session works on: the surface, the floor height, the scene it belongs to, and how an ack's item shows at once
 *  (the item.* events that follow change nothing more). */
interface Session {
  surface: DecorSurface; groundY: number; scene: SpaceScene | null;
  shown(item: ItemView, isNew: boolean): void; gone(id: ItemId): void; dispose(): void;
}

const inOwnHouse = (s: GameStore): boolean => !!s.you && typeof s.space === 'string' && s.space.startsWith(`house:${s.you.resident}:`);
function yardPlot(s: GameStore): Plot | null {
  const me = s.you ? s.residents?.[s.you.resident] : undefined;
  return (me && s.map?.plots[me.plot]) ?? null;
}
/** Outdoors and standing inside your own plot. */
function inOwnYard(s: GameStore): boolean {
  if (!s.you || s.space !== 'out') return false;
  const plot = yardPlot(s), p = plot ? getGame().you()?.pos : undefined;
  return !!plot && !!p && inRect(plot.rect, p.x, p.y);
}

// DESIGN.md decorate grid and footprint (presentation): cream lines at .5, leaf-700 square at 28 %.
const GRID = '#FFFBF1', FOOT = '#3A7D44';
/** How far above the floor the Turn / Put-away controls float (world units): just above a lifted piece of furniture. */
const CONTROLS_ABOVE = 1.35;
/** …or this far beside it when above and below are both off limits. */
const CONTROLS_BESIDE = 0.3;

function roomSession(game: Game, space: SpaceId): Session | null {
  const scene = game.scenes.current;
  if (!isRoomScene(scene)) return null;
  const surface: DecorSurface = {
    kind: 'room', space, origin: { x: 0, y: 0 },
    get size() { return { w: scene.view.w, h: scene.view.h }; },
    placed: () => scene.placed(),
    check: (cand) => surfaceCheck(scene.view, scene.placed())(cand),
    objectFor: (id) => scene.objectFor(id),
    get group() { return scene.room.group; }, get grid() { return scene.room.grid; }, get foot() { return scene.room.foot; },
  };
  return { surface, groundY: 0, scene, shown: (item) => { void scene.addItem(item); }, gone: (id) => scene.removeItem(id), dispose: () => {} };
}

/** Your yard as a decorate surface: the plot rect (origin offset), a grid and footprint square of its own in the outdoor scene, and the
 *  server's yard rule (checkYardPlacement) over the yard pieces, ground items, world objects and unwalkable tiles. */
function yardSession(game: Game, s: GameStore): Session | null {
  const out = game.scenes.out, layer = itemsLayerOf(game), plot = yardPlot(s);
  if (!out || !layer || !plot) return null;
  const r = plot.rect;
  const group = new THREE.Group(); group.name = 'decorateYard';
  group.position.y = out.heightAt({ x: r.x + r.w / 2, y: r.y + r.h / 2 });
  const lines: number[] = [];
  for (let x = r.x; x <= r.x + r.w; x++) lines.push(x, 0.04, r.y, x, 0.04, r.y + r.h);
  for (let z = r.y; z <= r.y + r.h; z++) lines.push(r.x, 0.04, z, r.x + r.w, 0.04, z);
  const grid = new THREE.LineSegments(new THREE.BufferGeometry().setAttribute('position', new THREE.Float32BufferAttribute(lines, 3)),
    new THREE.LineBasicMaterial({ color: GRID, transparent: true, opacity: 0.5, depthWrite: false }));
  grid.visible = false;
  const foot = new THREE.Mesh(new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2),
    new THREE.MeshBasicMaterial({ color: FOOT, transparent: true, opacity: 0.28, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -4 }));
  foot.renderOrder = 10; foot.visible = false;
  group.add(grid, foot);
  out.layer('decorate').add(group);
  const placed = (): Placed[] => layer.items().flatMap((i) =>
    (i.at.loc === 'outdoors' && i.at.placed && inRect(r, i.at.x, i.at.y) ? [{ id: i.id, catalogId: i.catalogId, x: i.at.x, y: i.at.y, rot: i.at.rot }] : []));
  const blocked = (t: TileXY): boolean => {                            // the server's tileBlocked + objectsAt + ground items
    const nav = game.nav();
    if (!nav || t.x < 0 || t.y < 0 || t.x >= nav.w || t.y >= nav.h || nav.walk[t.y * nav.w + t.x] !== 1) return true;
    if (game.objects().some((o) => { const e = getEntry(o.catalogId); return inRect(footprintRect(o.x, o.y, e?.kind === 'world' ? e.footprint : [1, 1], o.rot), t.x, t.y); })) return true;
    return layer.occupied(t);
  };
  const surface: DecorSurface = {
    kind: 'yard', space: 'out', origin: { x: r.x, y: r.y }, size: { w: r.w, h: r.h }, placed,
    check: (cand) => checkYardPlacement(plot, placed(), blocked, cand),
    objectFor: (id) => layer.viewOf(id), group, grid, foot,
  };
  return {
    surface, groundY: group.position.y, scene: game.scenes.current,
    shown: (item, isNew) => { if (!isNew) void layer.update(item); },  // a new yard piece arrives with its item.added event
    gone: (id) => layer.remove(id),
    dispose: () => { group.parent?.remove(group); grid.geometry.dispose(); (grid.material as THREE.Material).dispose(); foot.geometry.dispose(); (foot.material as THREE.Material).dispose(); },
  };
}

/** The screen box of a floor rectangle (tiles x…x+w, y…y+d) at the given heights. */
function screenBox(x: number, y: number, w: number, d: number, heights: readonly number[], at: (x: number, y: number, z: number) => { x: number; y: number }) {
  const ps = heights.flatMap((hy) => [at(x, hy, y), at(x + w, hy, y), at(x, hy, y + d), at(x + w, hy, y + d)]);
  const xs = ps.map((p) => p.x), ys = ps.map((p) => p.y);
  return { left: Math.min(...xs), right: Math.max(...xs), top: Math.min(...ys), bottom: Math.max(...ys) };
}

/** Takes decorate mode off the screen stack (Done, leaving the space, a rebuilt scene), wherever it sits. */
const closeDecorate = () => useGame.setState((s) => ({ screens: s.screens.filter((x) => x.id !== 'decorate') }));
const EMPTY: readonly ItemView[] = [];

const CSS = `
.deco-done { position: absolute; right: calc(var(--hud-inset) + env(safe-area-inset-right)); top: calc(var(--hud-inset) + env(safe-area-inset-top)); }
.deco-title { position: absolute; left: calc(var(--hud-inset) + env(safe-area-inset-left)); top: calc(var(--hud-inset) + env(safe-area-inset-top)); }
.deco-acts { position: absolute; display: none; gap: 1.2cqw; }
.deco-acts .round-btn { width: clamp(44px, 5.4cqw, 72px); height: clamp(44px, 5.4cqw, 72px); }
.deco-tray { position: absolute; left: 50%; bottom: calc(var(--hud-inset) + env(safe-area-inset-bottom)); transform: translateX(-50%); width: min(66cqw, 900px); }
.deco-tray .panel { max-width: none; max-height: 42cqh; padding: 24px 16px 12px; display: flex; flex-direction: column; gap: 8px; overflow: hidden; }
.deco-tray .tabs { flex: none; flex-wrap: nowrap; overflow-x: auto; scrollbar-width: none; }
.deco-tray .tab { white-space: nowrap; }
.deco-list { flex: 1 1 auto; min-height: 0; overflow-y: auto; overscroll-behavior: contain; }
.deco-grid { display: grid; grid-template-columns: repeat(8, 1fr); gap: 1cqw; }
.deco-note { flex: none; margin: 0; font-weight: 800; color: var(--wood-700); }
.deco-rows { display: grid; gap: 6px; }
.deco-row { display: grid; grid-template-columns: 40px 1fr auto; align-items: center; gap: 12px; background: var(--cream-100); border-radius: 16px;
  padding: 4px 6px 4px 4px; box-shadow: var(--sh-well); }
.deco-row img { width: 40px; height: 40px; object-fit: contain; }
.deco-row .btn { padding: 8px 16px 9px; font-size: 15px; }
.hud [data-hud]:not([role='status']), .hud .hud-pad { visibility: hidden; }
`;

function TrayCell({ item, selected, onPick }: { item: ItemView; selected: boolean; onPick(): void }) {
  const icon = useIcon(item.catalogId);
  const e = getEntry(item.catalogId);
  return <Slot label={e ? itemName(e.name) : item.catalogId} selected={selected} qty={item.qty} onClick={onPick}><img src={icon} alt="" /></Slot>;
}
function SurfaceRow({ item, onApply, busy }: { item: ItemView; onApply(): void; busy: boolean }) {
  const t = useT();
  const icon = useIcon(item.catalogId);
  const e = getEntry(item.catalogId);
  return (
    <div className="deco-row">
      <img src={icon} alt="" />
      <span style={{ fontWeight: 900 }}>{e ? itemName(e.name) : item.catalogId}</span>
      <Button disabled={busy} onClick={onApply}>{t('decor.apply')}</Button>
    </div>
  );
}

function Tray({ kind, deco, surfaces }: { kind: 'room' | 'yard'; deco: RefObject<Decorator | null>; surfaces: { index: number; wallpaper: string; floor: string } | null }) {
  const t = useT();
  const { items, tab, held, set } = useGame(useShallow((s) => ({ items: s.you?.items ?? EMPTY, tab: s.decorateTab, held: s.decorateHeld?.item.id ?? null, set: s.decorateSet })));
  const [busy, setBusy] = useState(false);
  const here: Tab = kind === 'yard' ? 'furniture' : tab;
  const list = trayItems(items, kind, here);
  const tabs: { id: Tab; label: string }[] = [
    { id: 'furniture', label: t('decor.tabFurniture') }, { id: 'rugs', label: t('decor.tabRugs') },
    { id: 'wallpaper', label: t('decor.tabWallpaper') }, { id: 'floor', label: t('decor.tabFloors') },
  ];
  const apply = (item: ItemView) => {
    if (!surfaces) return;
    setBusy(true);
    void getGame().cmd.act('room.surface', { item: item.id, room: surfaces.index }).then(() => setBusy(false));
  };
  const onSurface = here === 'wallpaper' || here === 'floor';
  const currentId = surfaces ? (here === 'wallpaper' ? surfaces.wallpaper : surfaces.floor) : null;
  const current = currentId ? getEntry(currentId) : undefined;
  return (
    <div className="deco-tray">
      <Panel title={t(kind === 'yard' ? 'hud.pockets' : 'decor.tray')}>
        {kind === 'room' && <Tabs tabs={tabs} active={here} onChange={(id) => set({ decorateTab: id as Tab })} />}
        {onSurface && current && <p className="deco-note">{t(here === 'wallpaper' ? 'decor.current' : 'decor.currentFloor', { name: t(current.name as StringKey) })}</p>}
        {list.length === 0 && <p className="deco-note">{t('decor.emptyTray')}</p>}
        {list.length > 0 && <div className="deco-list">{onSurface
          ? <div className="deco-rows">{list.map((i) => <SurfaceRow key={i.id} item={i} busy={busy} onApply={() => apply(i)} />)}</div>
          : <div className="deco-grid">{list.map((i) => <TrayCell key={i.id} item={i} selected={held === i.id} onPick={() => void deco.current?.take(i)} />)}</div>}</div>}
      </Panel>
    </div>
  );
}

function TurnIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 12a8 8 0 1 1-2.3-5.6" /><path d="M20 4v5h-5" /></svg>; }
function PutAwayIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9h16v10a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" /><path d="M12 3v9" /><path d="M8.5 8.5 12 12l3.5-3.5" /></svg>; }

function Decorate() {
  const t = useT();
  // Opened from the Menu, decorate mode replaces the panels below it, so the room stays in view (its key changes, so it mounts afresh).
  const bottom = useGame((s) => s.screens[0]?.id === 'decorate');
  const deco = useRef<Decorator | null>(null);
  const acts = useRef<HTMLDivElement>(null);
  const [kind, setKind] = useState<'room' | 'yard' | null>(null);
  const [surfaces, setSurfaces] = useState<{ index: number; wallpaper: string; floor: string } | null>(null);
  useEffect(() => {
    if (bottom) return;
    useGame.setState((s) => ({ screens: s.screens.slice(s.screens.findIndex((x) => x.id === 'decorate')) }));
  }, [bottom]);
  useEffect(() => {
    if (!bottom) return;
    const game = getGame(), s = useGame.getState();
    const canvas = game.renderer?.renderer.domElement, camera = game.camera?.camera;
    const session = canvas && camera ? (inOwnHouse(s) ? roomSession(game, s.space) : inOwnYard(s) ? yardSession(game, s) : null) : null;
    if (!session || !canvas || !camera) { closeDecorate(); return; }
    const { surface } = session, cmd = game.cmd;
    const d = createDecorator({
      camera, canvas, surface,
      ghost: (item) => (getEntry(item.catalogId)?.kind === 'fish' ? Promise.resolve(buildTank(getItem(item.catalogId) as FishEntry)) : game.assets.model(item.catalogId)),
      async commit(h: Held) {
        const q = requestFor(h, surface.space);
        const r = q.type === 'furniture.place' ? await cmd.act(q.type, q.req) : await cmd.act(q.type, q.req);
        if (r.ok) session.shown(r.r.item, h.from === null);
        return r.ok;
      },
      async stash(h: Held) {                                            // M1-T11: a yard piece goes back to pockets, never to storage
        const r = await cmd.act('furniture.store', { item: h.item.id, to: surface.kind === 'room' ? 'storage' : 'pockets' });
        if (r.ok) session.gone(h.item.id);
        return r.ok;
      },
      sfx: (name) => game.sound.sfx(name),
      onChange: (h, valid) => useGame.getState().decorateSet({ decorateHeld: h, decorateValid: valid }),
    });
    d.enter();
    deco.current = d;
    setKind(surface.kind);
    useGame.getState().decorateSet({ decorateActive: true, decorateHeld: null, decorateValid: true });
    const space = s.space;
    const unsub = useGame.subscribe((st) => { if (st.space !== space) closeDecorate(); });
    const v = new THREE.Vector3();
    let raf = 0, last = '', framed = '';
    const tick = () => {
      raf = requestAnimationFrame(tick);
      if (game.scenes.current !== session.scene) { closeDecorate(); return; }   // a resync rebuilt the room
      const cur = game.scenes.current;
      if (isRoomScene(cur)) {
        const key = `${cur.view.index}|${cur.view.wallpaper}|${cur.view.floor}`;
        if (key !== last) { last = key; setSurfaces({ index: cur.view.index, wallpaper: cur.view.wallpaper, floor: cur.view.floor }); }
      }
      const el = acts.current, root = el?.parentElement;
      if (!el || !root) return;
      const rect = canvas.getBoundingClientRect(), edge = 8;
      const screenAt = (x: number, y: number, z: number) => {
        v.set(x, y, z).project(camera);
        return { x: rect.left + ((v.x + 1) / 2) * rect.width, y: rect.top + ((1 - v.y) / 2) * rect.height };
      };
      // The safe box: below the top HUD row (the title pill, Done), above the tray, inside the safe-area edges.
      const hud = [...root.querySelectorAll('.deco-title, .deco-done')].map((x) => x.getBoundingClientRect());
      const hudBottom = Math.max(...hud.map((x) => x.bottom), 0);
      // the tray's top edge, with its title tab standing up over it
      const trayTop = Math.min(innerHeight, ...[...root.querySelectorAll('.deco-tray, .deco-tray .title-tab')].map((x) => x.getBoundingClientRect().top));
      if (surface.kind === 'room') {                                   // the room framed above the tray, its floor clear of the HUD
        const ui = { trayTop: Math.round(trayTop), walls: useGame.getState().decorateTab === 'wallpaper' ? 'full' as const : 'low' as const,
          hud: hud.map((x) => ({ left: Math.round(x.left), top: Math.round(x.top), right: Math.round(x.right), bottom: Math.round(x.bottom) })) };
        const key = JSON.stringify(ui);
        if (key !== framed) { framed = key; game.frameForDecorate(ui); }
      }
      const floor = screenBox(surface.origin.x, surface.origin.y, surface.size.w, surface.size.h, [session.groundY], screenAt);
      const floorKey = [floor.left, floor.top, floor.right, floor.bottom].map(Math.round).join(',');
      if (root.dataset.floor !== floorKey) root.dataset.floor = floorKey;   // read by the panel-fit e2e
      const h = useGame.getState().decorateHeld;
      if (!h) { el.style.display = 'none'; return; }
      const fp = footprintOf({ catalogId: h.item.catalogId, rot: h.rot });
      const lift = session.groundY + tuning.decor.liftHeight, cx = h.x + fp.w / 2, cz = h.y + fp.d / 2;
      el.style.display = 'flex';
      const front = screenAt(cx, session.groundY, h.y + fp.d);   // the footprint's front edge on the floor
      const mid = screenAt(cx, lift, cz).y;
      const pos = placeFloating({
        above: screenAt(cx, lift + CONTROLS_ABOVE, cz),
        below: { x: front.x, y: front.y + edge },
        right: screenAt(h.x + fp.w + CONTROLS_BESIDE, lift, cz),
        left: screenAt(h.x - CONTROLS_BESIDE, lift, cz),
        outRight: { x: floor.right + edge * 2, y: mid },
        outLeft: { x: floor.left - edge * 2, y: mid },
      }, { w: el.offsetWidth, h: el.offsetHeight }, { top: hudBottom + edge, bottom: trayTop - edge, left: edge, right: innerWidth - edge }, {
        soft: [floor],
        // the piece and the tiles around it, where the next tap is likely to go
        hard: [screenBox(h.x - 1, h.y - 1, fp.w + 2, fp.d + 2, [session.groundY, lift], screenAt)],
      });
      el.style.left = `${pos.left}px`;
      el.style.top = `${pos.top}px`;
      el.dataset.side = pos.side;
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf); unsub(); d.exit(); session.dispose(); deco.current = null; game.frameForDecorate(null);
      useGame.getState().decorateSet({ decorateActive: false, decorateHeld: null, decorateValid: true });
    };
  }, [bottom]);
  if (!bottom || !kind) return null;
  return (
    <div className="decorate" style={{ position: 'absolute', inset: 0 }}>
      <style>{CSS}</style>
      <div className="deco-title pill">{t(kind === 'yard' ? 'decor.yardTitle' : 'decor.title')}</div>
      <div className="deco-done hit"><Button onClick={closeDecorate}>{t('decor.done')}</Button></div>
      <div ref={acts} className="deco-acts">
        <RoundButton label={t('decor.turn')} onClick={() => deco.current?.rotate()}><TurnIcon /></RoundButton>
        <RoundButton label={t('decor.putAway')} onClick={() => void deco.current?.putAway()}><PutAwayIcon /></RoundButton>
      </div>
      <Tray kind={kind} deco={deco} surfaces={surfaces} />
    </div>
  );
}

export const screen: ScreenDef<'decorate'> = {
  id: 'decorate', layer: 'overlay', component: () => <Decorate />,
  menu: { label: 'decor.title', order: 20, visible: (s) => inOwnHouse(s) || inOwnYard(s) },
};
